import { http } from '@/services/api';
import type { Region } from '@/types';

export type RegionPayload = Pick<Region, 'name' | 'description' | 'city_id' | 'active'>;

export interface RegionStatistics {
  contacts: number;
  addresses: number;
  teams: number;
}

export const regionsService = {
  async list(): Promise<Region[]> {
    const data = await http.get<any[]>('/regions/list');
    return (Array.isArray(data) ? data : []).map((r) => {
      const idNum = Number(r?.id);
      const cityIdRaw = r?.city_id;
      const city_id = cityIdRaw === undefined || cityIdRaw === null || cityIdRaw === ''
        ? null
        : (Number.isFinite(Number(cityIdRaw)) ? Number(cityIdRaw) : null);
      return {
        id: Number.isFinite(idNum) ? idNum : r?.id,
        name: String(r?.name ?? ''),
        description: String(r?.description ?? ''),
        city_id,
        active: Boolean(r?.active ?? true),
        created_at: String(r?.created_at ?? ''),
        updated_at: r?.updated_at ? String(r.updated_at) : undefined,
      } as Region;
    });
  },

  async create(payload: RegionPayload): Promise<Region> {
    return http.post<Region>('/regions', payload);
  },

  async update(id: number, payload: RegionPayload): Promise<Region> {
    return http.put<Region>(`/regions/${id}`, payload);
  },

  async remove(id: number): Promise<{ message?: string }> {
    return http.delete<{ message?: string }>(`/regions/${id}`);
  },

  async statistics(id: number): Promise<RegionStatistics> {
    const raw = await http.get<Partial<RegionStatistics> & { teams_count?: number; contacts_count?: number; addresses_count?: number }>(`/regions/${id}/statistics`);
    // Map backend keys (e.g., teams_count, contacts_count, addresses_count) to expected shape
    const contacts = Number((raw as any)?.contacts ?? raw?.contacts_count ?? 0);
    const addresses = Number((raw as any)?.addresses ?? raw?.addresses_count ?? 0);
    const teams = Number((raw as any)?.teams ?? raw?.teams_count ?? 0);
    return { contacts, addresses, teams } as RegionStatistics;
  },

  async users(id: number): Promise<Array<{ id: number; name: string; team_ids: number[] }>> {
    const res = await http.get<{ data?: Array<{ id?: number | string; name?: string; team_ids?: Array<number | string> }> }>(`/regions/${id}/users`);
    const arr = Array.isArray(res?.data) ? res.data : [];
    const norm = arr.map((u) => ({
      id: Number(u?.id),
      name: String(u?.name ?? ''),
      team_ids: Array.isArray(u?.team_ids) ? (u!.team_ids as Array<number | string>).map((t) => Number(t)).filter((n) => Number.isFinite(n)) : []
    }))
      .filter((u) => Number.isFinite(u.id) && u.name.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
    return norm;
  }
};
